import Component from '@glimmer/component';
import { guidFor } from '@ember/object/internals';
import { PhInfo } from 'ember-phosphor-icons';
import Popover from './popover';

/** Field width. 'sm' | 'md' | 'lg' grow to share the row evenly; 'auto' hugs
 *  its content (use for checkboxes / toggles). */
export type FilterFieldWidth = 'sm' | 'md' | 'lg' | 'auto';

interface FilterFieldSignature {
  Args: {
    /**
     * Label rendered above the control. Omit for a control that carries its
     * own inline label (e.g. a checkbox) — the label row is still reserved so
     * the control stays on the same baseline as its labelled siblings.
     */
    label?: string;
    /**
     * Hint text rendered below the control. It grows the field downward only,
     * so it never shifts the label/control row out of alignment.
     */
    hint?: string;
    /**
     * Optional guidance shown via a hover/focus info-icon (ⓘ) tooltip beside
     * the label. Use this instead of `@hint` when below-input helper text would
     * disrupt the alignment of a horizontal filter row — the tooltip adds no
     * layout height, so the row stays aligned. Keyboard-accessible (the trigger
     * is focusable). Backward-compatible: omit it for the default behavior.
     */
    labelTooltip?: string;
    /** Field width. @default 'md' */
    width?: FilterFieldWidth;
  };
  Blocks: {
    /**
     * Yields `{ id }`. Bind it to the control's id so the field's `<label for>`
     * gives the control its accessible name (pass the control WITHOUT its own
     * `@label`).
     */
    default: [{ id: string }];
  };
  Element: HTMLDivElement;
}

/**
 * FilterField — the standardized cell for a single control in a filter bar.
 *
 * Drop these into `<PageControlBar>`'s `:filters` slot (or any flex row that
 * top-aligns its children) so every filter shares one label baseline, one
 * control baseline, and a consistent width — whether or not a control carries
 * a hint or has a label of its own.
 *
 * Pass each control WITHOUT its own `@label` and bind the yielded `id`, so the
 * field owns the label and the control still gets an accessible name.
 *
 * @example Labelled select
 * ```gts
 * <FilterField @label="Status" as |f|>
 *   <Select @id={{f.id}} @items={{this.states}} @selected={{this.status}} @onChange={{this.setStatus}} />
 * </FilterField>
 * ```
 *
 * @example A field with a hint
 * ```gts
 * <FilterField @label="Program" @hint="Select a single company first" as |f|>
 *   <Select @id={{f.id}} @items={{this.programs}} @selected={{this.program}} @onChange={{this.setProgram}} />
 * </FilterField>
 * ```
 *
 * @example A field whose guidance is a label tooltip (keeps the row aligned)
 * ```gts
 * <FilterField @label="Program" @labelTooltip="Select a single company first" as |f|>
 *   <Select @id={{f.id}} @items={{this.programs}} @selected={{this.program}} @onChange={{this.setProgram}} />
 * </FilterField>
 * ```
 *
 * @example A checkbox that keeps its own inline label (reserve the label row)
 * ```gts
 * <FilterField @width="auto">
 *   <Checkbox @label="Waiting on others" @checked={{this.waiting}} @onChange={{this.setWaiting}} />
 * </FilterField>
 * ```
 */
export default class FilterField extends Component<FilterFieldSignature> {
  /** Stable per-instance id for label[for] → control[id] association. */
  controlId = `filter-field-${guidFor(this)}`;

  get widthClass(): string {
    // `md` is the base `.app-filter-field` sizing — only non-default widths
    // get a modifier class (so the rendered class list never names a rule
    // that doesn't exist).
    const width = this.args.width ?? 'md';
    return width === 'md' ? '' : `app-filter-field--${width}`;
  }

  <template>
    <div class="app-filter-field {{this.widthClass}}" ...attributes>
      <div class="app-filter-field__label">
        {{#if @label}}
          {{#if @labelTooltip}}
            <div class="tio-row-extra-tight">
              <label
                data-test-filter-field-label
                class="tio-text-sm-medium tio-fg-secondary"
                for={{this.controlId}}
              >
                {{@label}}
              </label>
              <Popover @placement="top" @triggerMode="hover" @maxWidth={{280}}>
                <:trigger as |t|>
                  <button
                    data-test-filter-field-label-tooltip
                    type="button"
                    class="tio-filter-field__label-tooltip tio-row-flush shrink-0 p-0.5 rounded tio-fg-muted hover:tio-fg-secondary transition-colors"
                    aria-label={{@labelTooltip}}
                    aria-describedby={{t.panelId}}
                    {{t.register}}
                    {{t.hoverHandlers}}
                  >
                    <PhInfo @size={{16}} aria-hidden="true" />
                  </button>
                </:trigger>
                <:body>
                  {{@labelTooltip}}
                </:body>
              </Popover>
            </div>
          {{else}}
            <label
              data-test-filter-field-label
              class="block tio-text-sm-medium tio-fg-secondary"
              for={{this.controlId}}
            >
              {{@label}}
            </label>
          {{/if}}
        {{/if}}
      </div>

      <div class="app-filter-field__control">
        {{yield (hash id=this.controlId)}}
      </div>

      {{#if @hint}}
        <p data-test-filter-field-hint class="mt-1 text-xs tio-fg-muted">{{@hint}}</p>
      {{/if}}
    </div>
  </template>
}

export type { FilterFieldSignature };
